import React, { useEffect, useRef, useState } from "react";
import "./CSS/Stock.css";
import { backend_url } from "../App";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { menuItems } from "../Constants/menuItems";
import { exportCsv } from "../utils/exportCsv";
import { getAdminId } from "../utils/roleUtils";

const STATUS_TABS = ["All", "In-Stock", "Low Stock", "Out of Stock"];
const DOT_COLORS = [
  "#7c3aed","#0891b2","#059669","#f59e0b","#ef4444",
  "#8b5cf6","#06b6d4","#10b981","#f97316","#ec4899",
  "#6366f1","#14b8a6","#84cc16","#a855f7","#fb923c",
];
const CAT_TABS = [
  { label: "All Categories", value: "all" },
  ...menuItems.map((m) => ({ label: m.label, value: m.value })),
];
const PER_PAGE = 12;

const getAutoStatus = (qty, category) => {
  const n = Number(qty);
  if (isNaN(n) || n === 0) return "Out of Stock";
  if (category?.toLowerCase() === "sarees") {
    if (n < 3) return "Low Stock";
    return "In-Stock";
  }
  if (n < 6) return "Out of Stock";
  if (n < 15) return "Low Stock";
  return "In-Stock";
};

const getScale = (product) =>
  product.category?.toLowerCase() === "sarees" ? "Pcs" : product.scale;

const badgeClass = (status) => {
  if (status === "In-Stock") return "instock";
  if (status === "Low Stock") return "lowstock";
  return "outstock";
};

const Stock = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeSubcategory, setActiveSubcategory] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editProduct, setEditProduct] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [subcatOpen, setSubcatOpen] = useState(false);
  const subcatRef = useRef(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (subcatRef.current && !subcatRef.current.contains(e.target)) {
        setSubcatOpen(false);
      }
      if (statusRef.current && !statusRef.current.contains(e.target)) {
        setStatusOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${backend_url}/allproducts`);
      const data = await res.json();
      setAllProducts(data);
      setFiltered(data);
    } catch {
      toast.error("Failed to load products");
    }
  };

  /* get flat subcategory list for active category */
  const subcatList = (() => {
    if (activeCategory === "all") return [];
    const cat = menuItems.find((m) => m.value === activeCategory);
    if (!cat) return [];
    return cat.dropdown.flatMap((d) =>
      d.subcategories ? d.subcategories : (d.value ? [d] : [])
    );
  })();

  useEffect(() => {
    let list = allProducts;
    if (activeCategory !== "all")
      list = list.filter((p) => p.category?.toLowerCase() === activeCategory.toLowerCase());
    if (activeSubcategory !== "all")
      list = list.filter((p) => p.subcategory?.toLowerCase() === activeSubcategory.toLowerCase());
    if (activeTab !== "All")
      list = list.filter((p) => getAutoStatus(p.quantity, p.category) === activeTab);
    if (search.trim())
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.unique_id.toLowerCase().includes(search.toLowerCase())
      );
    setFiltered(list);
    setPage(1);
  }, [activeTab, activeCategory, activeSubcategory, search, allProducts]);

  /* counts scoped to category + subcategory */
  const scoped = allProducts
    .filter((p) => activeCategory === "all" || p.category?.toLowerCase() === activeCategory.toLowerCase())
    .filter((p) => activeSubcategory === "all" || p.subcategory?.toLowerCase() === activeSubcategory.toLowerCase());

  const counts = {
    all:      scoped.length,
    inStock:  scoped.filter((p) => getAutoStatus(p.quantity, p.category) === "In-Stock").length,
    lowStock: scoped.filter((p) => getAutoStatus(p.quantity, p.category) === "Low Stock").length,
    outStock: scoped.filter((p) => getAutoStatus(p.quantity, p.category) === "Out of Stock").length,
  };

  const catCount = (val) =>
    val === "all" ? allProducts.length
    : allProducts.filter((p) => p.category?.toLowerCase() === val.toLowerCase()).length;

  const subcatCount = (val) =>
    val === "all"
      ? allProducts.filter((p) => p.category?.toLowerCase() === activeCategory.toLowerCase()).length
      : allProducts.filter(
          (p) => p.category?.toLowerCase() === activeCategory.toLowerCase()
               && p.subcategory?.toLowerCase() === val.toLowerCase()
        ).length;

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleExportStock = () => {
    const header = ["Product ID", "Name", "Category", "Quantity", "Scale", "Status"];
    const rows = filtered.map((p) => [
      p.unique_id,
      p.name,
      p.category,
      p.quantity ?? "",
      getScale(p),
      getAutoStatus(p.quantity, p.category),
    ]);
    exportCsv("stock.csv", header, rows);
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setEditQty(product.quantity ?? "");
    setEditStatus(getAutoStatus(product.quantity, product.category));
  };

  const closeEdit = () => {
    setEditProduct(null);
    setEditQty("");
    setStatusOpen(false);
    setEditStatus("");
  };

  const saveStock = async () => {
    if (!editProduct) return;
    setSaving(true);
    try {
      const res = await fetch(`${backend_url}/updatestock/${editProduct._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quantity: editQty,
          status: editStatus,
          adminId: getAdminId(),
          adminName: sessionStorage.getItem("adminName"),
          adminRole: sessionStorage.getItem("adminRole"),
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Stock updated successfully");
        setAllProducts((prev) =>
          prev.map((p) =>
            p._id === editProduct._id
              ? { ...p, quantity: editQty, status: editStatus }
              : p
          )
        );
        closeEdit();
      } else {
        toast.error("Failed to update stock");
      }
    } catch {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="stock-page">
        {/* Header */}
        <div className="stock-header">
          <div className="stock-title-row">
            <h1 className="stock-title">
              <span>Stock Management</span>
            </h1>
            <span className="stock-total-badge">{counts.all} products</span>
            <button
              className="stock-export-btn"
              onClick={handleExportStock}
              disabled={!filtered.length}
            >
              ⬇ Export CSV
            </button>
          </div>

          <div className="stock-controls-row">
            <div className="stock-tabs">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab}
                  className={`stock-tab-btn ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="stock-search-wrap">
              <span className="stock-search-icon">&#128269;</span>
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Category tabs */}
          <div className="stock-cat-tabs-wrap">
            {CAT_TABS.map((cat) => (
              <button
                key={cat.value}
                className={`stock-cat-tab ${activeCategory === cat.value ? "active" : ""}`}
                onClick={() => { setActiveCategory(cat.value); setActiveSubcategory("all"); setActiveTab("All"); }}
              >
                {cat.label}
                <span className="stock-cat-count">{catCount(cat.value)}</span>
              </button>
            ))}
          </div>

          {/* Subcategory custom dropdown — visible only when a category is selected */}
          {subcatList.length > 0 && (
            <div className="stock-subcat-dropdown-wrap" ref={subcatRef}>
              <span className="stock-subcat-dropdown-label">Subcategory</span>
              <div className="stock-subcat-custom-dd">
                <button
                  className={`stock-subcat-trigger ${subcatOpen ? "open" : ""}`}
                  onClick={() => setSubcatOpen((v) => !v)}
                >
                  <span className="stock-subcat-trigger-text">
                    {activeSubcategory === "all"
                      ? "All Subcategories"
                      : subcatList.find((s) => s.value === activeSubcategory)?.label || "All Subcategories"}
                  </span>
                  <span className="stock-subcat-trigger-chevron">{subcatOpen ? "▲" : "▼"}</span>
                </button>

                {subcatOpen && (
                  <div className="stock-subcat-panel">
                    {/* All option */}
                    <button
                      className={`stock-subcat-opt ${activeSubcategory === "all" ? "active" : ""}`}
                      onClick={() => { setActiveSubcategory("all"); setActiveTab("All"); setSubcatOpen(false); }}
                    >
                      <span className="stock-subcat-opt-dot" style={{ background: "#5d4ac0" }} />
                      <span className="stock-subcat-opt-label">All Subcategories</span>
                      <span className={`stock-subcat-opt-count ${activeSubcategory === "all" ? "active" : ""}`}>
                        {subcatCount("all")}
                      </span>
                    </button>

                    {subcatList.map((s, i) => (
                      <button
                        key={s.value}
                        className={`stock-subcat-opt ${activeSubcategory === s.value ? "active" : ""}`}
                        onClick={() => { setActiveSubcategory(s.value); setActiveTab("All"); setSubcatOpen(false); }}
                      >
                        <span className="stock-subcat-opt-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} />
                        <span className="stock-subcat-opt-label">{s.label}</span>
                        <span className={`stock-subcat-opt-count ${activeSubcategory === s.value ? "active" : ""}`}>
                          {subcatCount(s.value)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {activeSubcategory !== "all" && (
                <button
                  className="stock-subcat-clear"
                  onClick={() => { setActiveSubcategory("all"); setActiveTab("All"); }}
                >
                  ✕ Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Stat Cards */}
        <div className="stock-stats-row">
          <div className="stock-stat-card scard-total">
            <div className="stock-stat-icon">&#128230;</div>
            <div className="stock-stat-info">
              <span className="stock-stat-label">Total Products</span>
              <span className="stock-stat-value">{counts.all}</span>
            </div>
          </div>
          <div className="stock-stat-card scard-instock">
            <div className="stock-stat-icon">&#9989;</div>
            <div className="stock-stat-info">
              <span className="stock-stat-label">In Stock</span>
              <span className="stock-stat-value">{counts.inStock}</span>
            </div>
          </div>
          <div className="stock-stat-card scard-lowstock">
            <div className="stock-stat-icon">&#9888;&#65039;</div>
            <div className="stock-stat-info">
              <span className="stock-stat-label">Low Stock</span>
              <span className="stock-stat-value">{counts.lowStock}</span>
            </div>
          </div>
          <div className="stock-stat-card scard-outstock">
            <div className="stock-stat-icon">&#10060;</div>
            <div className="stock-stat-info">
              <span className="stock-stat-label">Out of Stock</span>
              <span className="stock-stat-value">{counts.outStock}</span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="stock-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Category</th>
                <th>Quantity</th>
                <th>Status</th>
                <th>Update Stock</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length > 0 ? (
                paginated.map((product, idx) => (
                  <tr key={product._id}>
                    <td>
                      <div className="stock-serial">
                        {(page - 1) * PER_PAGE + idx + 1}
                      </div>
                    </td>
                    <td>
                      <div className="stock-product-cell">
                        <img
                          src={product.images?.[0]}
                          alt={product.name}
                          className="stock-product-thumb"
                        />
                        <div className="stock-product-info">
                          <span className="stock-product-id">
                            {product.unique_id}
                          </span>
                          <span className="stock-product-name">
                            {product.name}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{product.category}</td>
                    <td>
                      <div className="qty-cell">
                        <span className="qty-value">
                          {product.quantity ?? "—"}
                        </span>
                        <span className="qty-scale">{getScale(product)}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`stock-badge ${badgeClass(getAutoStatus(product.quantity, product.category))}`}
                      >
                        {getAutoStatus(product.quantity, product.category)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-edit-stock"
                        onClick={() => openEdit(product)}
                      >
                        Edit Stock
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="stock-empty">
                      <div className="stock-empty-icon">&#128230;</div>
                      <div>No products found</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="stock-pagination-wrap">
            <ul className="stock-pagination">
              <li
                className={`stock-page-item ${page === 1 ? "disabled" : ""}`}
              >
                <span
                  className="stock-page-link"
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                >
                  &laquo;
                </span>
              </li>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (n) =>
                    n === 1 ||
                    n === totalPages ||
                    (n >= page - 2 && n <= page + 2)
                )
                .map((n) => (
                  <li
                    key={n}
                    className={`stock-page-item ${page === n ? "active" : ""}`}
                  >
                    <span
                      className="stock-page-link"
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </span>
                  </li>
                ))}
              <li
                className={`stock-page-item ${
                  page === totalPages ? "disabled" : ""
                }`}
              >
                <span
                  className="stock-page-link"
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                >
                  &raquo;
                </span>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Edit Stock Modal */}
      {editProduct && (
        <div className="stock-modal-overlay" onClick={closeEdit}>
          <div
            className="stock-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="stock-modal-header">
              <h3>&#128230; Edit Stock</h3>
              <button className="stock-modal-close" onClick={closeEdit}>
                &times;
              </button>
            </div>

            <div className="stock-modal-body">
              <div className="stock-modal-product-row">
                <img
                  src={editProduct.images?.[0]}
                  alt={editProduct.name}
                  className="stock-modal-product-img"
                />
                <div>
                  <div className="stock-modal-product-name">
                    {editProduct.name}
                  </div>
                  <div className="stock-modal-product-id">
                    {editProduct.unique_id}
                  </div>
                </div>
              </div>

              <div className="stock-form-group">
                <label>Quantity ({getScale(editProduct)})</label>
                <input
                  type="number"
                  min="0"
                  value={editQty}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditQty(val);
                    setEditStatus(getAutoStatus(val, editProduct.category));
                  }}
                  placeholder="Enter quantity"
                />
              </div>

              <div className="stock-form-group">
                <label>Stock Status <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none', fontSize: '11px' }}>(auto-set by quantity)</span></label>
                <div className="stock-status-dd" ref={statusRef}>
                  <button
                    type="button"
                    className={`stock-status-trigger ${badgeClass(editStatus)} ${statusOpen ? "open" : ""}`}
                    onClick={() => setStatusOpen((v) => !v)}
                  >
                    <span className={`stock-status-dot ${badgeClass(editStatus)}`}></span>
                    <span className="stock-status-trigger-text">{editStatus}</span>
                    <span className="stock-status-chevron">{statusOpen ? "▲" : "▼"}</span>
                  </button>

                  {statusOpen && (
                    <div className="stock-status-panel">
                      {["In-Stock", "Low Stock", "Out of Stock"].map((s) => (
                        <button
                          type="button"
                          key={s}
                          className={`stock-status-opt ${badgeClass(s)} ${editStatus === s ? "active" : ""}`}
                          onClick={() => { setEditStatus(s); setStatusOpen(false); }}
                        >
                          <span className={`stock-status-dot ${badgeClass(s)}`}></span>
                          <span className="stock-status-opt-label">{s}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="stock-threshold-hint">
                {editProduct.category?.toLowerCase() === "sarees" ? (
                  <>
                    <span className="thr-dot out"></span> 0 — Out of Stock &nbsp;
                    <span className="thr-dot low"></span> 1–2 — Low Stock &nbsp;
                    <span className="thr-dot in"></span> 3+ — In-Stock
                  </>
                ) : (
                  <>
                    <span className="thr-dot out"></span> &lt; 6 — Out of Stock &nbsp;
                    <span className="thr-dot low"></span> 6–14 — Low Stock &nbsp;
                    <span className="thr-dot in"></span> 15+ — In-Stock
                  </>
                )}
              </div>
            </div>

            <div className="stock-modal-footer">
              <button className="btn-stock-cancel" onClick={closeEdit}>
                Cancel
              </button>
              <button
                className="btn-stock-save"
                onClick={saveStock}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Stock"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default Stock;
